import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { getSettingsApi } from '../apis';
import { getGeneralPath, getTenantId } from '@/utils/functions';
import {
  clearLocalStorage,
  localStorageLabels,
  useUtilFunctions,
} from '@/utils';
import { useAppStateContext } from '@/shared';
import IDLE_HORIZONTAL from '../../../assets/ph_horizontal.png';
import IDLE_VERTICAL from '../../../assets/ph_vertical.png';
import { clearBundle } from '../lib/bundleStore';
import { useLogger } from './useLogger';
import { useScheduleBundle } from './useScheduleBundle';
import { usePlayback } from './usePlayback';
import { useTimelinePlayback } from './useTimelinePlayback';
import { useSyncedClock } from './useSyncedClock';
import { useRealtime } from './useRealtime';
import { useContentPreload } from './useContentPreload';
import { itemLabel } from '../lib/items';
import { platform } from '@/platform';
import { RESET_EVENT } from '@/platform/shared/keys';

const NO_SLOTS = [];

const getScreenDimensions = () => ({
  width: window.screen.width,
  height: window.screen.height,
  availWidth: window.screen.availWidth,
  availHeight: window.screen.availHeight,
  orientation: window.screen.orientation,
});

/**
 * The paired player: composes the schedule bundle (offline-capable), playback, media preloading
 * and realtime. Returns the view state PagePlayer renders.
 */
export const usePagePlayer = () => {
  const { appState, setAppState } = useAppStateContext();
  const { screenInfo } = appState;
  const { updateScreenMeta } = useUtilFunctions();
  const { LogToExternal } = useLogger();
  const navigate = useNavigate();

  const tid = getTenantId();
  const isPaired = Boolean(screenInfo?.accessToken);

  const schedule = useScheduleBundle({
    enabled: isPaired,
    // Unpaired/deleted on the server: forget this screen and go back to pairing.
    onUnauthorized: () => {
      clearBundle();
      Promise.resolve(clearLocalStorage(localStorageLabels.DEVICE_INFO)).then(
        () => window.location.reload(),
      );
    },
  });

  // A video wall tile plays on the wall's shared timeline; any other screen item by item.
  const wall = schedule.bundle?.wall ?? null;
  const syncedNow = useSyncedClock({
    enabled: isPaired && !!wall,
    seedOffsetMs: schedule.clockOffsetMs,
  });
  const itemPlayback = usePlayback({
    schedules: wall ? NO_SLOTS : schedule.schedules,
    localNow: schedule.localNow,
    onSlotEnded: schedule.reevaluate,
  });
  const wallPlayback = useTimelinePlayback({
    schedules: wall ? schedule.schedules : NO_SLOTS,
    localNow: schedule.localNow,
    timeZone: schedule.bundle?.timeZone,
    now: syncedNow,
    onSlotEnded: schedule.reevaluate,
  });
  const playback = wall ? wallPlayback : itemPlayback;

  // "Identify" from the console: show this tile's place in its wall for a moment.
  const [identify, setIdentify] = useState(null);
  useEffect(() => {
    if (!identify) return;
    const timer = setTimeout(() => setIdentify(null), identify.seconds * 1000);
    return () => clearTimeout(timer);
  }, [identify]);

  // The tenant's own idle images are stored with the media so they show offline too.
  const config = appState?.localData?.config;
  const placeholderUrls = [
    config?.placeholderHorizontal &&
      getGeneralPath(tid) + config.placeholderHorizontal,
    config?.placeholderVertical &&
      getGeneralPath(tid) + config.placeholderVertical,
  ];

  const { isWarming } = useContentPreload({
    bundle: schedule.bundle,
    tid,
    extraUrls: placeholderUrls,
  });

  // What the console's live monitor sees.
  const liveSnapshot = useMemo(() => {
    if (!screenInfo) return null;
    const { scheduleId, playlist } = playback.activeItem;
    const isPlaying = scheduleId > 0 && !!playback.currentItem;
    const idleName = screenInfo.orientation === 1 ? 'idle_hor' : 'idle_ver';
    return {
      isIdle: !isPlaying,
      playlist: isPlaying ? playlist.name : 'idle',
      file: isPlaying ? itemLabel(playback.currentItem) : idleName,
      screenInfo: getScreenDimensions(),
      thumbnail: idleName,
      animation: {
        transitionEntry: playlist?.transitionEntry || 'animate__fadeIn',
        transitionExit: playlist?.transitionExit || 'animate__fadeOut',
      },
    };
  }, [playback.activeItem, playback.currentItem, screenInfo?.orientation]);

  useRealtime({
    enabled: isPaired,
    tid,
    screenId: screenInfo?.screenId,
    onScheduleUpdated: schedule.refresh,
    onIdentify: ({ label, seconds }) =>
      setIdentify({ label, seconds: seconds || 15 }),
    liveSnapshot,
  });

  const getIdleImage = (orientation) => {
    if (!orientation) return '';
    const generalPath = getGeneralPath(appState.tid);

    if (orientation === 1)
      return config?.placeholderHorizontal
        ? platform.media.resolve(generalPath + config.placeholderHorizontal)
        : IDLE_HORIZONTAL;
    return config?.placeholderVertical
      ? platform.media.resolve(generalPath + config.placeholderVertical)
      : IDLE_VERTICAL;
  };

  // Tenant branding/config (logo + custom idle placeholders); falls back to PixelPlay defaults.
  const loadTenantSettings = async () => {
    try {
      const response = await getSettingsApi();
      if (response.status === 200) {
        setAppState((draft) => {
          draft.localData = {
            ...draft.localData,
            config: { ...draft.localData?.config, ...response.data },
          };
        });
      }
    } catch (error) {
      LogToExternal('error', error);
    }
  };

  useEffect(() => {
    // Home shows the pairing screen instead of this page until the screen is paired.
    if (!isPaired) return;

    loadTenantSettings();
    updateScreenMeta({ screenId: screenInfo.screenId });

    // Re-pair this screen: clear local session, then show the pairing screen. Triggered by the
    // remote's Stop key, or by the host (e.g. a long press on Fire TV, which has no Stop key).
    const reset = () => navigate('/reset');
    const handleKeyPress = (event) => {
      if (platform.resetKeyCodes.includes(event.keyCode)) {
        event.preventDefault();
        reset();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    window.addEventListener(RESET_EVENT, reset);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      window.removeEventListener(RESET_EVENT, reset);
    };
  }, []);

  return {
    state: {
      // Only block the screen while there is nothing to play yet (first boot / first warm-up).
      isBusy: (!schedule.bundle && schedule.isFetching) || isWarming,
      bundle: schedule.bundle,
      activeItem: playback.activeItem,
      currentIndex: playback.currentIndex,
      items: playback.items,
      currentItem: playback.currentItem,
      advance: playback.advance,
      wall,
      now: syncedNow,
      itemStartedAtMs: playback.itemStartedAtMs,
      identify: identify?.label ?? null,
      clock: schedule.bundle
        ? {
            offsetMs: schedule.clockOffsetMs,
            timeZone: schedule.bundle.timeZone,
          }
        : null,
    },
    getIdleImage,
  };
};
